import React from 'react';
import { motion } from 'framer-motion';
import { Users, Zap, Wrench, Factory, TrendingUp } from 'lucide-react';

const Infrastructure = () => {
  const capacityData = [
    { label: 'Heavy + Rolled Section', value: '500 MT/Month', icon: Factory },
    { label: 'Built-up Section', value: '400 MT/Month', icon: TrendingUp },
  ];

  const machinery = [
    { category: 'Lifting Equipment', items: [
      { name: 'Hydra Cranes', count: '4' },
      { name: 'Chain Pulley Blocks', count: 'Multiple' },
      { name: 'Slings & Rigging Equipment', count: 'Complete Set' },
    ]},
    { category: 'Welding & Cutting', items: [
      { name: 'MIG Machines', count: '15' },
      { name: 'ARC/Rectifier', count: '40' },
      { name: 'Transformer Machines', count: '25' },
      { name: 'PUG Machines', count: '10' },
      { name: 'Gas Cutting Sets', count: '50' },
      { name: 'Grinding Machines', count: '25' },
      { name: 'Magnetic Drilling', count: '8' },
    ]},
  ];

  const workforce = [
    { role: 'Skilled Workers', count: '300+' },
    { role: 'Semi-Skilled Workers', count: '200+' },
    { role: 'Supervisors & Engineers', count: '25+' },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20" data-testid="infra-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-block border-l-4 border-orange-500 pl-4 mb-4">
              <p className="text-orange-500 font-oswald tracking-widest text-sm">INFRASTRUCTURE</p>
            </div>
            <h1 className="font-oswald font-bold text-5xl md:text-6xl mb-6">CAPACITY & CAPABILITIES</h1>
            <p className="text-xl text-slate-300 max-w-3xl">
              State-of-the-art machinery and skilled workforce to handle large-scale industrial projects
            </p>
          </motion.div>
        </div>
      </section>

      {/* Fabrication Capacity */}
      <section className="py-24 bg-white" data-testid="capacity-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-oswald font-bold text-4xl text-slate-900 mb-4">FABRICATION CAPACITY</h2>
            <p className="text-slate-600">Monthly production capabilities for structural steel</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {capacityData.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 p-8 border-l-4 border-orange-500 shadow-md"
                data-testid={`capacity-${index}`}
              >
                <item.icon className="w-16 h-16 text-orange-500 mb-4" />
                <h3 className="font-oswald font-bold text-2xl text-slate-900 mb-2">{item.label}</h3>
                <p className="font-oswald text-4xl text-orange-500 font-bold">{item.value}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-orange-50 border-l-4 border-orange-500">
            <p className="text-slate-700">
              <strong>Note:</strong> Capacity depends on job site location and project specifications. 
              We customize our production schedule to meet client deadlines.
            </p>
          </div>
        </div>
      </section>

      {/* Machinery */}
      <section className="py-24 bg-slate-50" data-testid="machinery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-oswald font-bold text-4xl text-slate-900 mb-4">MACHINERY & EQUIPMENT</h2>
            <p className="text-slate-600">Advanced tools and equipment for precision fabrication and erection</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {machinery.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 border-l-4 border-orange-500 shadow-sm"
                data-testid={`machinery-category-${index}`}
              >
                <div className="flex items-center mb-6">
                  {index === 0 ? <Wrench className="w-10 h-10 text-orange-500 mr-3" /> : <Zap className="w-10 h-10 text-orange-500 mr-3" />}
                  <h3 className="font-oswald font-bold text-2xl text-slate-900">{section.category}</h3>
                </div>
                <ul className="space-y-3">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex justify-between items-center py-2 border-b border-slate-200">
                      <span className="text-slate-700">{item.name}</span>
                      <span className="font-oswald font-bold text-orange-500">{item.count}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Workforce */}
      <section className="py-24 bg-white" data-testid="workforce-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-oswald font-bold text-4xl text-slate-900 mb-4">OUR WORKFORCE</h2>
            <p className="text-slate-600">Experienced professionals committed to quality and safety</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {workforce.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 bg-slate-50 border-l-4 border-orange-500 shadow-sm"
                data-testid={`workforce-${index}`}
              >
                <Users className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                <p className="font-oswald text-5xl font-bold text-slate-900 mb-2">{item.count}</p>
                <p className="text-slate-700 font-medium">{item.role}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-8 bg-slate-900 text-white border-l-4 border-orange-500"
          >
            <h3 className="font-oswald font-bold text-2xl mb-4">WORKFORCE HIGHLIGHTS</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                <span>Certified welders and fabricators</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                <span>Safety-trained personnel</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                <span>Experienced site engineers</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                <span>Dedicated quality control team</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Infrastructure;